import { useState, useEffect, useMemo } from "react";
import { ComposedChart, Area, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import SearchSelect from "./SearchSelect";
import { holtWintersForecast, aggregateHistoricalPricesByWeek, aggregateInternalPriceHistoryByWeek, aggregateTruckCrossingsByWeek, LBS_PER_TRUCK } from "./fobPricesForecast";
import { aggregateInternalPricesByWeek } from "./fobPricesCalc";
import { loadFromSupabase } from "../supabaseDB";

/* Forecast — Sub-fase D.2 / Fase A del roadmap "FOB PRICE FORECAST v2"
   (CLAUDE.md). Statistical baseline only: reads historical_prices (USDA/
   SNIIM — same table FobPricesRegistry.js already reads) and
   internal_price_history (Arturo's own client-vs-supplier price history,
   imported 2026-09-12 from a filled-in Excel — see scripts/import-
   internal-price-history.js), and projects whichever one is selected
   forward with fobPricesForecast.js's holtWintersForecast().

   historical_prices (USDA/SNIIM) only has 2026 YTD data today, so those
   two sources run in "trend-only" mode — see fobPricesForecast.js's
   header for why, and why that self-upgrades once the separately-tracked
   7-year backfill lands more rows into the same table, no code changes
   needed here. internal_price_history, by contrast, already has ~8 years
   of real weekly Blackberry data (2017-2025) — Product/Category/Size
   combinations with enough history there can already show a real
   SEASONAL forecast today, not just trend-only.

   internal_price_history does NOT overlap with the live invoices/bills
   data already feeding FobPricesRegistry.js's own Clients/Suppliers
   overlay (aggregateInternalPricesByWeek in fobPricesCalc.js) — the
   imported Excel stops 2025-05-23, before Nexus's own invoicing began
   (confirmed live: earliest real invoice is 2025-10-15). Both already
   only reflect Good Delivery sales — the Excel import only ever
   contained Good Delivery data (Arturo: "solo agregue las good
   delivery... los problemas de calidad no se consideran"), and
   aggregateInternalPricesByWeek already filters to saleStatus ===
   "goodDelivery" only. Client price and Supplier price are forecast as
   two separate Source options rather than blended — they represent
   different economic realities (GFI's revenue vs GFI's cost) and mixing
   them into one series would be meaningless.

   MERGED WITH LIVE NEXUS DATA (added 2026-09-16, Arturo: "porque el
   forecast no esta alimentandose de la temporada actual en cuanto a
   ventas de ground-fresh?" — he'd assumed this was already connected;
   the "GFI Internal" series stopped at the Excel's last week (~W21
   2025) and never picked up the current season's real invoices/bills
   that were already sitting in Nexus). `weeklyRows` below now
   concatenates the Excel history with a live aggregation of real
   invoices (Client Price) or bills (Supplier Price) via
   aggregateInternalPricesByWeek — same function FobPricesRegistry.js's
   Clients/Suppliers overlay already used, just never wired into the
   forecast model itself. Category needs a translation at the call site:
   the Excel uses "Organic"/"Conventional" (CATEGORY_LABEL below) while
   real invoice/bill lines use the short SKU codes "OG"/"CV" (verified
   against real data) — same physical thing, different spelling. The two
   date ranges don't overlap (Excel ends 2025-05-23, Nexus starts
   2025-10-15) so concatenation is safe; if they ever did, the live
   Nexus row wins for that week (it's the more authoritative source).

   Explicitly NOT built yet (see CLAUDE.md roadmap): Fase B (weather/
   holiday/fuel rule adjustments), Fase C (daily Claude API analysis
   layer), Fase D (accuracy feedback loop / AccuracyDashboard). This
   screen is the statistical foundation those phases will sit on top of.

   Filters are single-value (not multi like FobPricesRegistry.js) because
   a forecast projects ONE specific product/category/size series —
   blending several into one average before projecting would mix distinct
   price series into a meaningless trend. Options are derived from the
   real loaded rows (not a hardcoded list) so the dropdown never offers a
   value that doesn't actually exist in the underlying table. */

var T = {
  en: {
    title: "Forecast", subtitle: "Statistical FOB price projection from USDA/SNIIM and GFI's own price history",
    source: "Source", sourceUsda: "USDA (USD)", sourceSniim: "SNIIM (MXN)",
    sourceGfiClient: "GFI Internal — Client Price (USD)", sourceGfiSupplier: "GFI Internal — Supplier Price (USD)",
    product: "Product", category: "Category", size: "Size", horizon: "Horizon",
    horizon4: "4 weeks", horizon8: "8 weeks", horizon12: "12 weeks",
    period: "Period shown", periodAll: "All history", period1y: "Last 12 months", period2y: "Last 2 years", period3y: "Last 3 years", period5y: "Last 5 years",
    periodNote: "This only zooms the chart — the model still fits on all available history, not just what's shown.",
    chartTitle: "Price trend + projection",
    seriesActual: "Actual", seriesForecast: "Forecast", seriesBand90: "90% range",
    modeSeasonal: "Seasonal model active",
    modeTrendOnly: "Provisional trend-only projection",
    trendOnlyNote: "Only {points} of the ~104 weeks of history needed for a real seasonal forecast are available yet — showing a simple trend projection instead. This will switch to a full seasonal model automatically once more historical data is loaded, no changes needed here.",
    notEnoughData: "Not enough historical data yet for this Source/Product/Category/Size combination — pick a different filter, or wait for more data to be ingested.",
    pickFilters: "Select a Source, Product, Category and Size to see a projection.",
    pointsUsed: "Weeks of history used",
    truckChartTitle: "Mexico truck crossings (supply context)",
    truckChartNote: "Estimated trucks/week crossing from Mexico for this product (all border ports), based on USDA MARS movement data — " + LBS_PER_TRUCK.toLocaleString("en-US") + " lb per full truckload (26 pallets × 240 cases × 2.04 kg). Projected forward using the same Horizon as the price chart above, so both line up week-for-week. Not yet fed into the price model itself — shown for visual correlation.",
    truckSeriesName: "Trucks/week", truckSeriesForecastName: "Trucks/week (projected)", trucksUnit: "trucks",
  },
  es: {
    title: "Proyecciones", subtitle: "Proyección estadística de precio FOB a partir del historial USDA/SNIIM y del historial propio de GFI",
    source: "Fuente", sourceUsda: "USDA (USD)", sourceSniim: "SNIIM (MXN)",
    sourceGfiClient: "GFI Interno — Precio Cliente (USD)", sourceGfiSupplier: "GFI Interno — Precio Proveedor (USD)",
    product: "Producto", category: "Categoría", size: "Tamaño", horizon: "Horizonte",
    horizon4: "4 semanas", horizon8: "8 semanas", horizon12: "12 semanas",
    period: "Periodo a mostrar", periodAll: "Todo el historial", period1y: "Últimos 12 meses", period2y: "Últimos 2 años", period3y: "Últimos 3 años", period5y: "Últimos 5 años",
    periodNote: "Esto solo acerca la gráfica — el modelo sigue usando todo el historial disponible para ajustarse, no solo lo que se muestra.",
    chartTitle: "Tendencia de precio + proyección",
    seriesActual: "Real", seriesForecast: "Proyección", seriesBand90: "Rango 90%",
    modeSeasonal: "Modelo estacional activo",
    modeTrendOnly: "Proyección provisional (solo tendencia)",
    trendOnlyNote: "Solo hay {points} de las ~104 semanas de historial que se necesitan para un pronóstico estacional real — se muestra una proyección de tendencia simple en su lugar. Esto cambiará a un modelo estacional completo automáticamente en cuanto haya más historial cargado, sin necesidad de tocar nada aquí.",
    notEnoughData: "Todavía no hay suficiente historial para esta combinación de Fuente/Producto/Categoría/Tamaño — elige otro filtro, o espera a que se ingesten más datos.",
    pickFilters: "Selecciona Fuente, Producto, Categoría y Tamaño para ver una proyección.",
    pointsUsed: "Semanas de historial usadas",
    truckChartTitle: "Cruces de camiones desde México (contexto de oferta)",
    truckChartNote: "Camiones/semana estimados cruzando desde México para este producto (todos los puertos fronterizos), basado en datos de movimiento de USDA MARS — " + LBS_PER_TRUCK.toLocaleString("es-MX") + " lb por camión completo (26 pallets × 240 cajas × 2.04 kg). Proyectado hacia adelante con el mismo Horizonte que la gráfica de precio de arriba, para que ambas coincidan semana a semana. Aún no se usa dentro del modelo de precio — se muestra para correlación visual.",
    truckSeriesName: "Camiones/semana", truckSeriesForecastName: "Camiones/semana (proyectado)", trucksUnit: "camiones",
  },
};

var CHART_COLORS = { actual: "#2563EB", actualMid: "#0F172A", forecast: "#6B7280", band: "#9CA3AF" };

var compactSelectStyle = { width: 160 };

function isGfiSource(source) { return source === "GFI_CLIENT" || source === "GFI_SUPPLIER"; }

/* Excel history (internal_price_history) spells category out in full;
   real invoice/bill lines use the short SKU code (verified against real
   data: 'OG'/'CV', see CLAUDE.md's naming conventions). Same physical
   category, two spellings — translate at the boundary rather than
   changing either source. */
var CATEGORY_LABEL_TO_SHORT = { Organic: "OG", Conventional: "CV" };
var CATEGORY_SHORT_TO_LABEL = { OG: "Organic", CV: "Conventional" };

export default function ForecastRegistry({ lang: globalLang }) {
  var lang = globalLang || "es";
  var t = T[lang] || T.en;

  var [hpRows, setHpRows] = useState([]);
  var [internalRows, setInternalRows] = useState([]);
  var [truckRows, setTruckRows] = useState([]);
  var [nexusInvoices, setNexusInvoices] = useState([]);
  var [nexusBills, setNexusBills] = useState([]);
  var [loading, setLoading] = useState(true);
  var [priceSource, setPriceSource] = useState("USDA");
  var [selProduct, setSelProduct] = useState("");
  var [selCategory, setSelCategory] = useState("");
  var [selSize, setSelSize] = useState("");
  var [horizon, setHorizon] = useState(8);
  /* Display-only zoom (2026-09-12, Arturo: "para ver rangos de fechas,
     meses, periodos?") — narrows what's PLOTTED, never what the model
     FITS on. Filtering the model's own input by date would make a
     seasonal fit worse (throwing away real history it needs), not
     better — same reason FobPricesRegistry.js's own Date range filter
     only ever narrows its table/chart, never anything upstream. Default
     2 years: enough to see full-season shape without the chart being
     unreadable on an 8-year GFI-internal series like Arturo's imported
     Blackberry history. */
  var [displayPeriod, setDisplayPeriod] = useState("2y");

  useEffect(function () {
    setLoading(true);
    Promise.all([
      loadFromSupabase("historical_prices").catch(function () { return []; }),
      loadFromSupabase("internal_price_history").catch(function () { return []; }),
      loadFromSupabase("truck_crossings").catch(function () { return []; }),
      loadFromSupabase("invoices").catch(function () { return []; }),
      loadFromSupabase("bills").catch(function () { return []; }),
    ]).then(function (results) {
      setHpRows(results[0] || []);
      setInternalRows(results[1] || []);
      setTruckRows(results[2] || []);
      setNexusInvoices(results[3] || []);
      setNexusBills(results[4] || []);
    }).finally(function () { setLoading(false); });
  }, []);

  var gfi = isGfiSource(priceSource);
  var nexusRecords = priceSource === "GFI_CLIENT" ? nexusInvoices : nexusBills;

  var sourceRows = useMemo(function () {
    return gfi ? internalRows : hpRows.filter(function (r) { return r.source === priceSource; });
  }, [gfi, internalRows, hpRows, priceSource]);

  /* Options derived from real data so the dropdown never offers a
     Product/Category/Size combination that has zero rows behind it. When
     gfi, also scan the live Nexus invoices/bills (not just the Excel) so
     e.g. Blueberry/Raspberry — real in Nexus, never in the Excel — are
     selectable too. */
  var productOptions = useMemo(function () {
    var seen = {};
    sourceRows.forEach(function (r) { if (r.product) seen[r.product] = true; });
    if (gfi) (nexusRecords || []).forEach(function (rec) { (rec.lines || []).forEach(function (l) {
      var p = l.productBase || l.productName; if (p) seen[p] = true;
    }); });
    return Object.keys(seen).sort();
  }, [sourceRows, gfi, nexusRecords]);
  var categoryOptions = useMemo(function () {
    var seen = {};
    sourceRows.forEach(function (r) { if ((!selProduct || r.product === selProduct) && r.category) seen[r.category] = true; });
    if (gfi) (nexusRecords || []).forEach(function (rec) { (rec.lines || []).forEach(function (l) {
      var p = l.productBase || l.productName;
      var catLabel = CATEGORY_SHORT_TO_LABEL[l.category] || l.category;
      if ((!selProduct || p === selProduct) && catLabel) seen[catLabel] = true;
    }); });
    return Object.keys(seen).sort();
  }, [sourceRows, selProduct, gfi, nexusRecords]);
  var sizeOptions = useMemo(function () {
    var seen = {};
    sourceRows.forEach(function (r) {
      var sizeVal = gfi ? r.size : r.packageSize;
      if ((!selProduct || r.product === selProduct) && (!selCategory || r.category === selCategory) && sizeVal) seen[sizeVal] = true;
    });
    if (gfi) (nexusRecords || []).forEach(function (rec) { (rec.lines || []).forEach(function (l) {
      var p = l.productBase || l.productName;
      var catLabel = CATEGORY_SHORT_TO_LABEL[l.category] || l.category;
      if ((!selProduct || p === selProduct) && (!selCategory || catLabel === selCategory) && l.size) seen[l.size] = true;
    }); });
    return Object.keys(seen).sort();
  }, [sourceRows, selProduct, selCategory, gfi, nexusRecords]);

  var currencyPrefix = priceSource === "SNIIM" ? "MX$" : "$";

  /* SNIIM rows never carry a category (Zarzamora has no organic/
     conventional split reported — see sniim-ingest.js) — categoryOptions
     is always [] for that source, which used to make Category an
     un-satisfiable required filter and silently block SNIIM from ever
     producing a forecast (found 2026-09-15 while verifying the 7-year
     backfill: SNIIM had real data but the screen could never show it).
     When there's nothing to pick, category is simply not part of this
     source's granularity — treat it as already satisfied instead of
     requiring an impossible selection. */
  var categoryRequired = categoryOptions.length > 0;
  var filtersReady = !!selProduct && (!categoryRequired || !!selCategory) && !!selSize;

  var weeklyRows = useMemo(function () {
    if (!filtersReady) return [];
    if (gfi) {
      var priceField = priceSource === "GFI_CLIENT" ? "clientPrice" : "supplierPrice";
      var excelWeekly = aggregateInternalPriceHistoryByWeek(internalRows, priceField, selProduct, selCategory, selSize);

      /* Live Nexus invoices/bills — the current-season data Arturo
         assumed was already feeding this (it wasn't). Category needs
         translating to the short SKU code real lines actually use. */
      var liveWeekly = aggregateInternalPricesByWeek(nexusRecords, selProduct, CATEGORY_LABEL_TO_SHORT[selCategory] || selCategory, selSize)
        .map(function (r) { var p = Math.round(r.avgPrice * 100) / 100; return { year: r.year, weekIso: r.weekIso, priceLow: p, priceHigh: p }; });

      var byKey = {};
      excelWeekly.forEach(function (r) { byKey[r.year + "|" + r.weekIso] = r; });
      /* Live Nexus wins on any overlap — it's the more authoritative
         source — though in practice the two ranges don't overlap
         (Excel ends 2025-05-23, Nexus starts 2025-10-15). */
      liveWeekly.forEach(function (r) { byKey[r.year + "|" + r.weekIso] = r; });
      return Object.values(byKey).sort(function (a, b) {
        if (a.year !== b.year) return a.year - b.year;
        return a.weekIso < b.weekIso ? -1 : a.weekIso > b.weekIso ? 1 : 0;
      });
    }
    return aggregateHistoricalPricesByWeek(hpRows, priceSource, selProduct, selCategory, selSize);
  }, [filtersReady, gfi, hpRows, internalRows, nexusRecords, priceSource, selProduct, selCategory, selSize]);

  var granularityKey = filtersReady ? [priceSource, selProduct, selCategory, selSize].join("|") : "";

  /* Chart-only zoom — crops which ACTUAL points get plotted, keeps the
     forecast fit itself (below) on the full weeklyRows unchanged. Year-
     level cutoff (not exact ISO week math) is plenty precise for a zoom
     control. */
  var visibleWeeklyRows = useMemo(function () {
    if (displayPeriod === "all" || weeklyRows.length === 0) return weeklyRows;
    var yearsBack = { "1y": 1, "2y": 2, "3y": 3, "5y": 5 }[displayPeriod] || 2;
    var lastYear = weeklyRows[weeklyRows.length - 1].year;
    var cutoffYear = lastYear - yearsBack;
    return weeklyRows.filter(function (r) { return r.year >= cutoffYear; });
  }, [weeklyRows, displayPeriod]);

  /* Truck-crossing volume overlay (2026-09-15) — Mexico-origin only, so it
     applies the same regardless of priceSource (USDA/SNIIM/GFI Internal
     all describe the same physical berries). Filtered only by product
     (truck_crossings carries no category/size) — a supply-context signal
     next to the price chart, not yet fed into holtWintersForecast() as an
     explanatory variable (see CLAUDE.md roadmap Fase B). */
  var truckWeeklyRows = useMemo(function () {
    if (!selProduct) return [];
    return aggregateTruckCrossingsByWeek(truckRows, selProduct);
  }, [truckRows, selProduct]);

  var visibleTruckWeeklyRows = useMemo(function () {
    if (displayPeriod === "all" || truckWeeklyRows.length === 0) return truckWeeklyRows;
    var yearsBack = { "1y": 1, "2y": 2, "3y": 3, "5y": 5 }[displayPeriod] || 2;
    var lastYear = truckWeeklyRows[truckWeeklyRows.length - 1].year;
    var cutoffYear = lastYear - yearsBack;
    return truckWeeklyRows.filter(function (r) { return r.year >= cutoffYear; });
  }, [truckWeeklyRows, displayPeriod]);

  /* 2026-09-15 (Arturo): "el horizonte solo se mueve en precios no en
     camiones, debe ajustarse en ambos para poder comparar precios vs
     cruces" — the truck chart only ever showed past actuals, so its
     x-axis stopped at "today" while the price chart's kept extending
     `horizon` weeks into the future, making the two impossible to line
     up week-for-week. Reuses the exact same holtWintersForecast() as the
     price series — a point-value series (low=high=lbUnits, same
     convention aggregateInternalPriceHistoryByWeek already uses for
     GFI's own point-price history) with its own granularityKey so it
     never shares model state with the price forecast above. Best-effort:
     a product/date range with under 3 weeks of truck data just shows no
     projected bars rather than erroring out — this is a secondary
     context chart, not the main forecast. */
  var truckForecast = useMemo(function () {
    if (truckWeeklyRows.length < 3) return { rows: [] };
    try {
      var forModel = truckWeeklyRows.map(function (r) { return { year: r.year, weekIso: r.weekIso, priceLow: r.lbUnits, priceHigh: r.lbUnits }; });
      return holtWintersForecast(forModel, { horizon: horizon, granularityKey: "TRUCKS|" + selProduct });
    } catch (err) {
      return { rows: [] };
    }
  }, [truckWeeklyRows, horizon, selProduct]);

  var truckChartData = useMemo(function () {
    var actualPoints = visibleTruckWeeklyRows.map(function (r) {
      return { label: r.weekIso, trucks: Math.round((r.lbUnits / LBS_PER_TRUCK) * 10) / 10, forecastTrucks: null };
    });
    var forecastPoints = (truckForecast.rows || []).map(function (r) {
      return { label: r.weekIso, trucks: null, forecastTrucks: Math.round((r.forecast / LBS_PER_TRUCK) * 10) / 10 };
    });
    return actualPoints.concat(forecastPoints);
  }, [visibleTruckWeeklyRows, truckForecast]);

  var forecast = useMemo(function () {
    if (weeklyRows.length === 0) return { rows: [], mode: null, error: null };
    try {
      return Object.assign({ error: null }, holtWintersForecast(weeklyRows, { horizon: horizon, granularityKey: granularityKey }));
    } catch (err) {
      return { rows: [], mode: null, error: err && err.message ? err.message : String(err) };
    }
  }, [weeklyRows, horizon, granularityKey]);

  var chartData = useMemo(function () {
    var actualPoints = visibleWeeklyRows.map(function (r) {
      return {
        label: r.weekIso,
        actualBand: (r.priceLow != null && r.priceHigh != null) ? [r.priceLow, r.priceHigh] : null,
        actualMid: (r.priceLow != null && r.priceHigh != null) ? Math.round((r.priceLow + r.priceHigh) * 50) / 100 : null,
        forecastMid: null, forecastBand: null,
      };
    });
    var forecastPoints = (forecast.rows || []).map(function (r) {
      return {
        label: r.weekIso,
        actualBand: null, actualMid: null,
        forecastMid: r.forecast,
        forecastBand: [r.low90, r.high90],
      };
    });
    return actualPoints.concat(forecastPoints);
  }, [visibleWeeklyRows, forecast]);

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 14, marginBottom: 4 }}>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>{t.title}</h2>
          <p style={{ fontSize: 11, color: "var(--color-text-muted)", margin: "2px 0 0" }}>{t.subtitle}</p>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "flex-end" }}>
          <div>
            <div style={{ fontSize: 9, color: "var(--color-text-muted)", marginBottom: 2 }}>{t.source}</div>
            <SearchSelect value={priceSource} onChange={function (v) { setPriceSource(v); setSelProduct(""); setSelCategory(""); setSelSize(""); }} style={{ ...compactSelectStyle, minWidth: 160 }} options={[
              { value: "USDA", label: t.sourceUsda },
              { value: "SNIIM", label: t.sourceSniim },
              { value: "GFI_CLIENT", label: t.sourceGfiClient },
              { value: "GFI_SUPPLIER", label: t.sourceGfiSupplier },
            ]} />
          </div>
          <div>
            <div style={{ fontSize: 9, color: "var(--color-text-muted)", marginBottom: 2 }}>{t.product}</div>
            <SearchSelect value={selProduct} onChange={function (v) { setSelProduct(v); setSelCategory(""); setSelSize(""); }} placeholder={t.product} style={compactSelectStyle} options={productOptions.map(function (p) { return { value: p, label: p }; })} />
          </div>
          <div>
            <div style={{ fontSize: 9, color: "var(--color-text-muted)", marginBottom: 2 }}>{t.category}</div>
            <SearchSelect value={selCategory} onChange={function (v) { setSelCategory(v); setSelSize(""); }} placeholder={t.category} style={compactSelectStyle} options={categoryOptions.map(function (c) { return { value: c, label: c }; })} disabled={!selProduct} />
          </div>
          <div>
            <div style={{ fontSize: 9, color: "var(--color-text-muted)", marginBottom: 2 }}>{t.size}</div>
            <SearchSelect value={selSize} onChange={setSelSize} placeholder={t.size} style={compactSelectStyle} options={sizeOptions.map(function (s) { return { value: s, label: s }; })} disabled={categoryRequired && !selCategory} />
          </div>
          <div>
            <div style={{ fontSize: 9, color: "var(--color-text-muted)", marginBottom: 2 }}>{t.horizon}</div>
            <SearchSelect value={horizon} onChange={function (v) { setHorizon(parseInt(v, 10)); }} style={{ ...compactSelectStyle, minWidth: 110 }} options={[{ value: 4, label: t.horizon4 }, { value: 8, label: t.horizon8 }, { value: 12, label: t.horizon12 }]} />
          </div>
          <div>
            <div style={{ fontSize: 9, color: "var(--color-text-muted)", marginBottom: 2 }}>{t.period}</div>
            <SearchSelect value={displayPeriod} onChange={setDisplayPeriod} style={{ ...compactSelectStyle, minWidth: 150 }} options={[
              { value: "1y", label: t.period1y },
              { value: "2y", label: t.period2y },
              { value: "3y", label: t.period3y },
              { value: "5y", label: t.period5y },
              { value: "all", label: t.periodAll },
            ]} />
          </div>
        </div>
      </div>

      {!loading && !filtersReady && (
        <div style={{ padding: "12px 16px", borderRadius: 8, background: "rgba(37,99,235,0.08)", border: "1px solid rgba(37,99,235,0.25)", color: "#2563EB", fontSize: 12, fontWeight: 600, marginTop: 14 }}>{t.pickFilters}</div>
      )}

      {!loading && filtersReady && forecast.error && (
        <div style={{ padding: "12px 16px", borderRadius: 8, background: "rgba(220,38,38,0.08)", border: "1px solid rgba(220,38,38,0.25)", color: "#DC2626", fontSize: 12, fontWeight: 600, marginTop: 14 }}>{t.notEnoughData}</div>
      )}

      {!loading && forecast.mode && (
        <div style={{ marginTop: 14 }}>
          <div style={{ padding: "10px 14px", borderRadius: 8, marginBottom: 12, background: forecast.mode === "seasonal" ? "rgba(5,150,105,0.08)" : "rgba(217,119,6,0.08)", border: "1px solid " + (forecast.mode === "seasonal" ? "rgba(5,150,105,0.25)" : "rgba(217,119,6,0.25)") }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: forecast.mode === "seasonal" ? "#059669" : "#B45309" }}>{forecast.mode === "seasonal" ? t.modeSeasonal : t.modeTrendOnly}</div>
            {forecast.mode === "trend-only" && (
              <div style={{ fontSize: 11, color: "var(--color-text-secondary)", marginTop: 3 }}>{t.trendOnlyNote.replace("{points}", forecast.pointsUsed)}</div>
            )}
            <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginTop: 4 }}>{t.pointsUsed}: {forecast.pointsUsed}</div>
            {displayPeriod !== "all" && <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginTop: 2, fontStyle: "italic" }}>{t.periodNote}</div>}
          </div>

          <div style={{ background: "var(--color-bg-card)", borderRadius: 10, padding: 12, height: 340 }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={function (v) { return currencyPrefix + v; }} />
                <Tooltip formatter={function (v) { return Array.isArray(v) ? currencyPrefix + v[0] + " – " + currencyPrefix + v[1] : currencyPrefix + v; }} />
                <Legend />
                <Area type="monotone" dataKey="actualBand" name={t.seriesActual} stroke={CHART_COLORS.actual} fill={CHART_COLORS.actual} fillOpacity={0.15} connectNulls />
                <Line type="monotone" dataKey="actualMid" name={t.seriesActual + " (mid)"} stroke={CHART_COLORS.actualMid} strokeWidth={2} dot={false} connectNulls />
                <Area type="monotone" dataKey="forecastBand" name={t.seriesBand90} stroke={CHART_COLORS.band} fill={CHART_COLORS.band} fillOpacity={0.12} />
                <Line type="monotone" dataKey="forecastMid" name={t.seriesForecast} stroke={CHART_COLORS.forecast} strokeWidth={2} strokeDasharray="5 5" dot={{ r: 3 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          {truckChartData.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>{t.truckChartTitle}</div>
              <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginBottom: 8 }}>{t.truckChartNote}</div>
              <div style={{ background: "var(--color-bg-card)", borderRadius: 10, padding: 12, height: 180 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={truckChartData} margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Legend />
                    <Tooltip formatter={function (v) { return v + " " + t.trucksUnit; }} />
                    <Bar dataKey="trucks" name={t.truckSeriesName} fill={CHART_COLORS.actual} fillOpacity={0.55} />
                    <Bar dataKey="forecastTrucks" name={t.truckSeriesForecastName} fill={CHART_COLORS.forecast} fillOpacity={0.4} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
